import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { AdminStorageFeesFacade } from './admin-storage-fees.facade';
import {
  EffectiveStorageFeeSettings,
  StorageFeeInvoiceListItem,
  StorageFeeListFilter,
  StorageFeeOverridesDto,
  StorageFeeSettings,
  StorageFeeSettingsUpdateDto,
} from '@models/resources/storage-fee.model';

/**
 * HTTP-Variante der Admin-Lagergebühren-Facade (Sprint 7 S7-10..S7-14).
 *
 * Endpoint-Mapping siehe `admin-storage-fees.facade.ts`-Header. Alle
 * Endpoints leben unter `/api/storage-fees/*` bzw.
 * `/api/storage-fee-invoices/*` und sind staff-only (Backend enforced).
 *
 * Wire-Shapes matchen 1:1 das FE-Modell (`storage-fee.model.ts`) —
 * der Resource-Layer im Backend übernimmt das Mapping. Hier wird also
 * **nicht** gemappt, nur durchgereicht.
 *
 * Download + Resend gehen über den generischen Invoice-Endpoint
 * (`/api/invoices/{id}/download` und `/api/invoices/{id}/resend`),
 * der sowohl Transaktions- als auch Lagergebühr-Rechnungen behandelt
 * (siehe InvoiceController). Der `id` einer Storage-Fee-Invoice ist
 * direkt die Invoice-ID (S7-12 Resource).
 */
@Injectable()
export class HttpAdminStorageFeesFacade implements AdminStorageFeesFacade {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  getSettings(): Observable<StorageFeeSettings> {
    return this.http.get<StorageFeeSettings>(`${this.base}/storage-fees/settings`);
  }

  saveSettings(dto: StorageFeeSettingsUpdateDto): Observable<StorageFeeSettings> {
    return this.http.patch<StorageFeeSettings>(`${this.base}/storage-fees/settings`, dto);
  }

  getOverrides(customerId: number): Observable<StorageFeeOverridesDto> {
    return this.http.get<StorageFeeOverridesDto>(
      `${this.base}/customers/${customerId}/storage-fee-overrides`,
    );
  }

  saveOverrides(
    customerId: number,
    dto: StorageFeeOverridesDto,
  ): Observable<StorageFeeOverridesDto> {
    // Konzept §7.4 / Drift-Fix D4: PATCH (partial update pro Feld) — siehe
    // Route `admin.storage-fee-overrides.update`. PUT antwortet 405.
    return this.http.patch<StorageFeeOverridesDto>(
      `${this.base}/customers/${customerId}/storage-fee-overrides`,
      dto,
    );
  }

  getEffective(customerId: number): Observable<EffectiveStorageFeeSettings> {
    return this.http.get<EffectiveStorageFeeSettings>(
      `${this.base}/customers/${customerId}/storage-fee-overrides/effective`,
    );
  }

  list(filter: StorageFeeListFilter): Observable<StorageFeeInvoiceListItem[]> {
    const params: Record<string, string> = {};
    if (filter.status && filter.status !== 'all') params['status'] = filter.status;
    if (filter.customer_id != null) params['customer_id'] = String(filter.customer_id);
    if (filter.year != null) {
      // FE-Filter ist year (Kalenderjahr); BE versteht date_from/date_to
      params['date_from'] = `${filter.year}-01-01`;
      params['date_to'] = `${filter.year}-12-31`;
    }
    return this.http.get<StorageFeeInvoiceListItem[]>(`${this.base}/storage-fee-invoices`, {
      params,
    });
  }

  listForCustomer(customerId: number): Observable<StorageFeeInvoiceListItem[]> {
    return this.http.get<StorageFeeInvoiceListItem[]>(
      `${this.base}/customers/${customerId}/storage-fee-invoices`,
    );
  }

  markPaid(id: number): Observable<StorageFeeInvoiceListItem> {
    return this.http.post<StorageFeeInvoiceListItem>(
      `${this.base}/storage-fee-invoices/${id}/mark-paid`,
      {},
    );
  }

  markUnpaid(id: number): Observable<StorageFeeInvoiceListItem> {
    return this.http.post<StorageFeeInvoiceListItem>(
      `${this.base}/storage-fee-invoices/${id}/mark-unpaid`,
      {},
    );
  }

  markPaidBulk(ids: readonly number[]): Observable<StorageFeeInvoiceListItem[]> {
    return this.http
      .post<{ updated: StorageFeeInvoiceListItem[]; rejected: unknown[] }>(
        `${this.base}/storage-fee-invoices/mark-paid-bulk`,
        { ids: [...ids] },
      )
      .pipe(map((response) => response.updated));
  }

  download(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/invoices/${id}/download`, {
      responseType: 'blob',
    });
  }

  resend(id: number): Observable<void> {
    return this.http
      .post<{ resent: boolean }>(`${this.base}/invoices/${id}/resend`, {})
      .pipe(map(() => undefined));
  }
}
